import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { Alert, Stack } from '@mui/material';

import { useContentSettings, useUpdateContentSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import { CONTENT_LOCALE_OPTIONS } from '../options';
import {
  FeedbackSnackbar,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

/** Storefront content configuration. */
export function ContentSettingsPage() {
  const { canView, canManage } = useDomainPermissions('content');
  const query = useContentSettings();
  const update = useUpdateContentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Content settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Content settings"
      subtitle="Shop info section visibility, storefront pages, legal content, banners and SEO metadata."
      icon={<ArticleRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Shop info sections" icon={<StorefrontRoundedIcon />}>
              <Alert severity="info" variant="outlined">
                These toggles control which sections appear on the customer-facing "About shop"
                page in the Telegram Mini App. The Telegram contact handle itself is configured
                in <strong>Settings → Telegram → Business profile</strong>; business hours and
                other published page content are managed in <strong>Content → Pages</strong>.
              </Alert>
              <SwitchField
                label="Shop info page"
                description='Show the "About shop" page on the storefront.'
                checked={draft.shopInfoEnabled}
                onChange={(value) => form.setField('shopInfoEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Contact details block"
                description='Show the "Связаться с нами" (contact-us) block and company contact card on the shop-info page.'
                checked={draft.contactDetailsBlockEnabled}
                onChange={(value) => form.setField('contactDetailsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Payment methods block"
                checked={draft.paymentMethodsBlockEnabled}
                onChange={(value) => form.setField('paymentMethodsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Delivery options block"
                checked={draft.deliveryOptionsBlockEnabled}
                onChange={(value) => form.setField('deliveryOptionsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Return policy block"
                checked={draft.returnPolicyBlockEnabled}
                onChange={(value) => form.setField('returnPolicyBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Legal summary block"
                checked={draft.legalSummaryBlockEnabled}
                onChange={(value) => form.setField('legalSummaryBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Legal docs required"
                description="Require at least one published legal document before the storefront goes live."
                checked={draft.legalDocsRequired}
                onChange={(value) => form.setField('legalDocsRequired', value)}
                disabled={disabled}
              />
              <SelectField
                label="Default locale"
                description="Locale override for shop-info page content. Must match a published page's locale (ru/en) — leave blank to use the service default (Russian)."
                value={draft.defaultLocale ?? ''}
                options={CONTENT_LOCALE_OPTIONS}
                onChange={(value) => form.setField('defaultLocale', value || null)}
                disabled={disabled}
                allowEmpty
              />
            </SettingsCard>

            <SettingsCard title="Content modules" icon={<ArticleRoundedIcon />}>
              <SwitchField
                label="Storefront pages"
                checked={draft.storefrontPagesEnabled}
                onChange={(value) => form.setField('storefrontPagesEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Legal content"
                checked={draft.legalContentEnabled}
                onChange={(value) => form.setField('legalContentEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Banners"
                checked={draft.bannersEnabled}
                onChange={(value) => form.setField('bannersEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Landing pages"
                checked={draft.landingPagesEnabled}
                onChange={(value) => form.setField('landingPagesEnabled', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Publishing rule"
                value={draft.publishingRule}
                onChange={(value) => form.setField('publishingRule', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="SEO metadata defaults" icon={<TravelExploreRoundedIcon />}>
              <TextInputField
                label="Default title"
                value={draft.seoMetadataDefaults?.title ?? ''}
                onChange={(value) =>
                  form.setField('seoMetadataDefaults', {
                    ...draft.seoMetadataDefaults,
                    title: value || null,
                  })
                }
                disabled={disabled}
              />
              <TextInputField
                label="Default description"
                value={draft.seoMetadataDefaults?.description ?? ''}
                onChange={(value) =>
                  form.setField('seoMetadataDefaults', {
                    ...draft.seoMetadataDefaults,
                    description: value || null,
                  })
                }
                disabled={disabled}
                multiline
                rows={2}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
